import { spanAt, spanSuffix, spansIn, type SpanRole } from '@dispositio/core';
import { Mark, mergeAttributes } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';

/**
 * Bezug: Eine Textstelle gehört zu einem oder mehreren Elementen (sie sprechen, handeln, denken) – anders als eine Nennung (refLink)
 * nicht an ein Wort gebunden. Gespeichert als `[Textstelle]{@clara @paul}` bzw. mit Art `[„Komm!“]{@clara rede}`; Nennungen in der
 * Stelle bleiben (die Markierung steht im Schema nach refLink und umschließt sie deshalb beim Speichern). Nur in Textdokumenten.
 */
export const RefSpan = Mark.create({
  name: 'refSpan',
  inclusive: false,
  addAttributes() {
    return {
      keys: {
        default: [],
        parseHTML: (el) => (el.getAttribute('data-span') ?? '').split(' ').filter(Boolean),
        renderHTML: (attrs) => ({ 'data-span': (attrs.keys as string[]).join(' ') }),
      },
      role: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-role'),
        renderHTML: (attrs) => (attrs.role ? { 'data-role': attrs.role as string } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-span]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes({ class: 'rt-span' }, HTMLAttributes), 0];
  },

  markdownTokenizer: {
    name: 'refSpan',
    level: 'inline',
    start: (src: string) => spansIn(src)[0]?.from ?? -1,
    tokenize: (src, _tokens, lexer) => {
      const s = spanAt(src, 0);
      if (!s) return undefined;
      const inner = src.slice(s.textFrom, s.textTo);
      return { type: 'refSpan', raw: src.slice(0, s.to), text: inner, keys: s.keys, role: s.role, tokens: lexer.inlineTokens(inner) };
    },
  },
  parseMarkdown: (token, h) => {
    const t = token as { keys?: string[]; role?: SpanRole | null };
    return h.applyMark('refSpan', h.parseInline(token.tokens ?? []), { keys: t.keys ?? [], role: t.role ?? null });
  },
  renderMarkdown: (node, h) => {
    const attrs = (node.attrs ?? {}) as { keys?: string[]; role?: SpanRole | null };
    return `[${h.renderChildren(node)}]${spanSuffix(attrs.keys ?? [], attrs.role ?? null)}`;
  },
});

export interface SpanRange {
  from: number;
  to: number;
  keys: string[];
  role: SpanRole | null;
}

const sameSpan = (a: SpanRange, keys: string[], role: SpanRole | null): boolean => a.role === role && a.keys.join(' ') === keys.join(' ');

/** Alle Bezüge im Dokument (zusammenhängende Stellen mit gleichen Schlüsseln und gleicher Art). */
export function spanRanges(doc: PMNode): SpanRange[] {
  const out: SpanRange[] = [];
  let cur: SpanRange | null = null;
  doc.descendants((node, pos) => {
    if (!node.isInline) return true;
    const m = node.marks.find((x) => x.type.name === 'refSpan');
    const keys = (m?.attrs.keys as string[] | undefined) ?? [];
    const role = (m?.attrs.role as SpanRole | null | undefined) ?? null;
    if (m && cur && cur.to === pos && sameSpan(cur, keys, role)) cur.to = pos + node.nodeSize;
    else if (m) out.push((cur = { from: pos, to: pos + node.nodeSize, keys, role }));
    else cur = null;
    return false;
  });
  return out;
}

/** Bezug, der die Stelle ganz oder teilweise abdeckt (der erste) */
export const spanOver = (doc: PMNode, from: number, to: number): SpanRange | undefined => spanRanges(doc).find((s) => s.from < Math.max(to, from + 1) && s.to > from);
